<script>
import { setLocal, getLocal } from '@shared/services/Utils';
import utilsMixin from '@shared/mixins/utils';
import { paymentScheduleFrequencies } from '@shared/config/paymentSchedules';
import RegisterFormStepEmail from '@school/components/RegisterFormStepEmail.vue';
import RegisterFormStepInfos from '@school/components/RegisterFormStepInfos.vue';
import APITraining from '@school/services/API/Training';

export default {
  mixins: [utilsMixin],
  provide() {
    return {
      store: this.store,
      authCustomer: this.authCustomer,
    };
  },
  components: {
    RegisterFormStepEmail,
    RegisterFormStepInfos,
  },
  props: {
    isEmbed: {
      type: Boolean,
      default: false,
    },
    canStay: {
      type: Boolean,
      default: true,
    },
    buyable: {
      type: Object,
      required: true,
    },
    buyableName: {
      type: String,
      default: null,
    },
    hasModal: {
      type: Boolean,
      default: true,
    },
    hasCloseBtn: {
      type: Boolean,
      default: true,
    },
    hasPaymentPlans: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    messages() {
      return {
        frequencies: Object.keys(paymentScheduleFrequencies).reduce((result, frequency) => {
          const { i18nKey } = paymentScheduleFrequencies[frequency];
          result[frequency] = {
            name: this.$t(`${i18nKey}_frequency_name`),
            description: this.$t(`${i18nKey}_frequency_description`),
          };
          return result;
        }, {}),
      };
    },
    store() {
      return this.$store.state.store.data;
    },
    hasPercentPlan() {
      return this.$store.getters['store/hasPercentPlan'];
    },
    isNotPercentPlan() {
      return this.$store.getters['store/isNotPercentPlan'];
    },
    authCustomer() {
      return this.$store.getters['auth/customer'];
    },
    hasDiscountPrice() {
      if (this.buyable.buyable_type === 'MEMBERSHIP_PRICE') {
        const option = this.activeSubscriptionPrice;
        return this.subscriptionPriceHasDiscount(option);
      }

      return this.buyable.discount_price != null;
    },
    price() {
      if (this.buyable.buyable_type === 'MEMBERSHIP_PRICE') {
        const option = this.activeSubscriptionPrice;
        if (!option) {
          return 0;
        }

        return this.subscriptionPriceHasDiscount(option)
          ? option.discount_price
          : option.amount;
      }

      return this.hasDiscountPrice
        ? this.buyable.discount_price
        : this.buyable.price;
    },
    oldPrice() {
      if (
        this.buyable.buyable_type === 'MEMBERSHIP_PRICE'
        && (this.newPrice != null || this.hasDiscountPrice)
      ) {
        return this.activeSubscriptionPrice?.amount;
      }

      // Use originalPrice from API when available (for legacy cases)
      if (this.originalPrice != null) {
        return this.originalPrice;
      }

      const buyablePrice = this.buyable.price || this.buyable.amount;
      const oldChosenPrice = this.chosenPaymentPlan
        ? this.chosenPaymentPlan.amount
        : buyablePrice;
      return this.newPrice != null ? oldChosenPrice : buyablePrice;
    },
    chosenPrice() {
      return this.chosenPaymentPlan
        ? this.chosenPaymentPlan.amount
        : this.price;
    },
    displayPrice() {
      return this.newPrice != null ? this.newPrice : this.chosenPrice;
    },
    subscriptionPrices() {
      if (this.buyable.buyable_type !== 'MEMBERSHIP_PRICE') {
        return [];
      }

      return this.buyable.subscription?.prices || [];
    },
    hasSubscriptionPriceOptions() {
      return this.subscriptionPrices.length > 1;
    },
    activeSubscriptionPrice() {
      if (this.buyable.buyable_type !== 'MEMBERSHIP_PRICE') {
        return null;
      }

      const prices = this.subscriptionPrices;
      const targetUuid = this.selectedSubscriptionPriceUuid || this.buyable.uuid;
      const baseBuyable = this.buyable;
      const selectedPrice = (
        prices.find((price) => price.uuid === targetUuid)
        || prices.find((price) => price.uuid === this.buyable.uuid)
      );

      if (!selectedPrice) {
        return baseBuyable;
      }

      if (selectedPrice === baseBuyable) {
        return baseBuyable;
      }

      return {
        ...baseBuyable,
        ...selectedPrice,
        feature_options: {
          ...baseBuyable.feature_options,
          ...(selectedPrice.feature_options || {}),
        },
        subscription: {
          ...baseBuyable.subscription,
          prices,
        },
      };
    },
    currentIntervalSource() {
      if (this.buyable.buyable_type !== 'MEMBERSHIP_PRICE') {
        return this.buyable;
      }

      return this.activeSubscriptionPrice || this.buyable;
    },
    currentDiscountReason() {
      if (this.buyable.buyable_type !== 'MEMBERSHIP_PRICE') {
        return this.buyable.discount_price_reason;
      }

      return this.activeSubscriptionPrice?.discount_price_reason;
    },
    paymentSchedules() {
      const schedules = this.buyable && Array.isArray(this.buyable.payment_schedules)
        ? this.buyable.payment_schedules
        : [];
      return schedules;
    },
  },
  data() {
    const isNotPercentPlan = this.$store.getters['store/isNotPercentPlan'];

    return {
      isLoading: false,
      isEnrolling: false,
      step: 'email',
      newPrice: null,
      originalPrice: null,
      chosenPaymentPlan: null,
      priceSelectorIsOpen: isNotPercentPlan && this.hasPaymentPlans,
      subscriptionPriceSelectorIsOpen: (
        this.buyable.buyable_type === 'MEMBERSHIP_PRICE'
        && this.buyable.subscription
        && Array.isArray(this.buyable.subscription.prices)
        && this.buyable.subscription.prices.length > 1
      ),
      tempCustomer: null,
      customer: {
        confirmedIntent: null,
        email: {},
        infos: {},
      },
      selectedSubscriptionPriceUuid: this.buyable.buyable_type === 'MEMBERSHIP_PRICE'
        ? this.buyable.uuid
        : null,
    };
  },
  methods: {
    getAlreadyOwnedMessage() {
      if (this.buyable.buyable_type === 'TRAINING') {
        return this.$t('training_already_owned');
      }
      if (this.buyable.buyable_type === 'MEMBERSHIP_PRICE') {
        return this.$t('subscription_already_owned');
      }
      return this.$t('pack_already_owned');
    },
    getViewButtonText() {
      if (this.buyable.buyable_type === 'TRAINING') {
        return this.$t('view_training');
      }
      if (this.buyable.buyable_type === 'MEMBERSHIP_PRICE') {
        return this.$t('view_subscription');
      }
      return this.$t('view_pack');
    },
    togglePriceSelector() {
      this.priceSelectorIsOpen = !this.priceSelectorIsOpen;
      this.$nextTick(() => this.$emit('change'));
    },
    getSelectedMembershipPriceUuid() {
      if (this.buyable.buyable_type !== 'MEMBERSHIP_PRICE') {
        return null;
      }

      return this.selectedSubscriptionPriceUuid || this.buyable.uuid;
    },
    handleNext() {
      let handlePromise;

      if (this.step === 'email') {
        handlePromise = this.handleEmailStep();
      } else {
        handlePromise = this.handleInfosStep();
      }

      handlePromise.then(() => this.$nextTick(() => this.$emit('change')));
    },
    goToInfos() {
      this.step = 'infos';
      this.priceSelectorIsOpen = false;
      if (this.buyable.buyable_type === 'MEMBERSHIP_PRICE') {
        this.subscriptionPriceSelectorIsOpen = false;
      }
      const productId = this.getSelectedMembershipPriceUuid() || this.buyable.uuid;
      this.$gtag.checkoutProgress({
        coupon: this.customer.infos.discount_code,
        items: [
          {
            id: productId,
            name: this.buyable.name,
            quantity: 1,
            price: this.displayPrice,
          },
        ],
      });
      this.$fbq.lead({
        content_name: this.buyable.name,
        value: this.chosenPrice,
      });
    },
    subscriptionPriceHasDiscount(priceOption) {
      if (!priceOption) {
        return false;
      }

      return priceOption.discount_price
        && priceOption.discount_price > 0
        && priceOption.discount_price < priceOption.amount;
    },
    formatSubscriptionPrice(priceOption) {
      if (!priceOption) {
        return this.$options.filters.formatPrice(0);
      }

      const priceValue = this.subscriptionPriceHasDiscount(priceOption)
        ? priceOption.discount_price
        : priceOption.amount;

      return this.$options.filters.formatPrice(priceValue);
    },
    onSubscriptionPriceChange(priceUuid) {
      if (this.buyable.buyable_type !== 'MEMBERSHIP_PRICE') {
        return;
      }

      this.selectedSubscriptionPriceUuid = priceUuid;

      if (priceUuid === this.buyable.uuid) {
        return;
      }

      this.$router.replace({
        query: {
          ...this.$route.query,
          price: priceUuid,
        },
      });
    },
    toggleSubscriptionPriceSelector() {
      this.subscriptionPriceSelectorIsOpen = !this.subscriptionPriceSelectorIsOpen;
      this.$nextTick(() => this.$emit('change'));
    },
    handleComplete() {
      if (this.isEmbed) {
        this.$buefy.dialog.confirm({
          message: this.getAlreadyOwnedMessage(),
          confirmText: this.getViewButtonText(),
          cancelText: this.$t('stay_here'),
          focusOn: 'cancel',
          onCancel: () => this.$emit('close'),
          onConfirm: () => this.$emit('complete'),
        });
      } else {
        this.$buefy.notification.open({
          type: 'is-success',
          message: this.getAlreadyOwnedMessage(),
        });
        this.$emit('complete');
      }
    },
    async handleEnroll() {
      const { redirect } = this.buyable.feature_options;
      const productId = this.getSelectedMembershipPriceUuid() || this.buyable.uuid;

      setLocal(this.$constants.LSKEY_UTM_SOURCE, null, false, true);
      setLocal(this.$constants.LSKEY_UTM_MEDIUM, null, false, true);
      setLocal(this.$constants.LSKEY_UTM_CAMPAIGN, null, false, true);
      setLocal(this.$constants.LSKEY_UTM_ID, null, false, true);
      setLocal(this.$constants.LSKEY_UTM_TERM, null, false, true);
      setLocal(this.$constants.LSKEY_UTM_CONTENT, null, false, true);

      this.isEnrolling = true;
      const membershipReady = await this.waitForMembershipSync();

      if (this.hasPercentPlan || !redirect) {
        this.isEnrolling = false;
        if (this.isEmbed) {
          if (!membershipReady) {
            this.$buefy.toast.open({
              type: 'is-warning',
              message: this.$t('membership_activation_pending'),
              duration: 4000,
            });
          }
          const func = this.$buefy.dialog[this.canStay ? 'confirm' : 'alert'];
          func({
            message:
              this.buyable.buyable_type === 'TRAINING'
                ? this.$t('welcome_to_training')
                : this.$t('welcome'),
            confirmText:
              this.buyable.buyable_type === 'TRAINING'
                ? this.$t('view_training')
                : this.$t('view_trainings'),
            cancelText: this.$t('stay_here'),
            onCancel: () => this.$emit('close'),
            onConfirm: () => this.$emit('enroll'),
          });
        } else {
          const toastConfig = membershipReady
            ? {
              type: 'is-success',
              message: this.$t('welcome'),
            }
            : {
              type: 'is-warning',
              message: this.$t('membership_activation_pending'),
              duration: 4000,
            };
          this.$buefy.toast.open(toastConfig);
          this.$emit('enroll');
        }
      }

      this.$gtag.purchase({
        items: [
          {
            id: productId,
            name: this.buyable.name,
            quantity: 1,
            price: this.displayPrice,
          },
        ],
        value: this.displayPrice,
      });
      this.$fbq.purchase({
        content_ids: [productId],
        content_name: this.buyable.name,
        num_items: 1,
        value: this.displayPrice,
      });

      if (this.isNotPercentPlan && redirect) {
        if (this.isEmbed) {
          if (!membershipReady) {
            this.$buefy.toast.open({
              type: 'is-warning',
              message: this.$t('membership_activation_pending'),
              duration: 4000,
            });
          }
          this.isEnrolling = false;
          this.$emit('enroll');
          return;
        }

        if (!membershipReady) {
          this.$buefy.toast.open({
            type: 'is-warning',
            message: this.$t('membership_activation_pending'),
            duration: 4000,
          });
        }

        window.location.href = redirect;
      }

      if (this.isEnrolling) {
        this.isEnrolling = false;
      }
    },
    handleEmailStep(customerEmailInfos = null) {
      customerEmailInfos = customerEmailInfos || this.customer.email;
      this.isLoading = true;
      const membershipPriceUuid = this.getSelectedMembershipPriceUuid();
      return this.$store
        .dispatch('auth/register', {
          autoLogin: false,
          utm_data: this.getUTMData(),
          ...(this.buyable.buyable_type === 'TRAINING'
            ? { training_uuid: this.buyable.uuid }
            : {}),
          ...(this.buyable.buyable_type === 'BUNDLE'
            ? { bundle_uuid: this.buyable.uuid }
            : {}),
          ...(membershipPriceUuid
            ? {
              membership_price_uuid: membershipPriceUuid,
            }
            : {}),
          ...customerEmailInfos,
        })
        .then((customer) => {
          let result;
          this.tempCustomer = customer;

          switch (this.tempCustomer.enrollment_status) {
            case 'COMPLETED':
              this.handleComplete();
              break;
            case 'TRAINING_CLOSED':
              this.$buefy.notification.open({
                type: 'is-warning',
                message: this.$t('enrollment_closed'),
              });
              break;
            case 'CREATED':
            case 'IN_PROGRESS':
              if (this.chosenPrice <= 0) {
                if (this.tempCustomer.firstname) {
                  result = this.enroll();
                }
              }

              this.goToInfos(this.tempCustomer);
              break;
            case 'ATTACHED':
              if (this.chosenPrice > 0 || !this.tempCustomer.firstname) {
                this.goToInfos(this.tempCustomer);
                break;
              }

              result = this.enroll();
              break;
            case 'MAX_STUDENT_NUMBER_REACHED':
              this.$buefy.notification.open({
                type: 'is-danger',
                message: this.$t('training_full'),
              });
              break;

            default:
              break;
          }

          return result;
        })
        .finally(() => (this.isLoading = false));
    },
    enroll(data = {}) {
      this.isLoading = true;
      this.isEnrolling = true;

      const oldFirstname = this.tempCustomer && this.tempCustomer.firstname;
      const oldLastname = this.tempCustomer && this.tempCustomer.lastname;
      const affiliate_codes = this.hasPercentPlan
        ? undefined
        : getLocal(this.$constants.LSKEY_AFFILIATE_CODES, true, undefined);

      if (oldFirstname) {
        delete data.firstname;
        delete data.lastname;
        delete data.password;
      }

      if (
        this.displayPrice > 0
        && this.buyable.feature_options.billing_address.enabled
      ) {
        if (!data.name) {
          data.name = oldFirstname
            ? `${oldFirstname} ${oldLastname}`
            : `${data.firstname} ${data.lastname}`;
        }
      }

      const membershipPriceUuid = this.getSelectedMembershipPriceUuid();

      return this.$store
        .dispatch('auth/enroll', {
          buyable_type: this.buyable.buyable_type,
          customer_uuid: this.tempCustomer.uuid,
          ...(this.buyable.buyable_type === 'TRAINING'
            ? { training_uuid: this.buyable.uuid }
            : {}),
          ...(this.buyable.buyable_type === 'BUNDLE'
            ? { bundle_uuid: this.buyable.uuid }
            : {}),
          ...(membershipPriceUuid
            ? {
              membership_price_uuid: membershipPriceUuid,
            }
            : {}),
          affiliate_codes,
          utm_data: this.getUTMData(),
          payment_schedule_id: this.chosenPaymentPlan
            ? this.chosenPaymentPlan.id
            : undefined,
          ...data,
        })
        .then((data) => {
          if (data && data.requires_action) return data;
          return this.saveCustomFields().then(() => this.handleEnroll());
        })
        .finally(() => {
          this.isLoading = false;
          this.isEnrolling = false;
        });
    },
    saveCustomFields() {
      // Bundle custom fields are saved server-side via the enrollment payload
      // (CustomerEnrollmentService.finalizeBundlePurchase). The front-end
      // safety-net PUT here is training-only.
      if (this.buyable.buyable_type !== 'TRAINING') return Promise.resolve();
      const data = this.customer?.infos?.custom_fields_data;
      if (!data || !data.length) return Promise.resolve();
      return APITraining.submitCustomFieldAnswers(this.buyable.slug, data)
        .catch(() => null);
    },
    sendCheckoutStats() {
      const productId = this.getSelectedMembershipPriceUuid() || this.buyable.uuid;
      this.$gtag.checkoutProgress({
        coupon: this.customer.infos.discount_code,
        items: [
          {
            id: productId,
            name: this.buyable.name,
            quantity: 1,
            price: this.displayPrice,
          },
        ],
      });
      this.$fbq.initiateCheckout({
        content_ids: [productId],
        num_items: 1,
        value: this.displayPrice,
      });
    },
    handleInfosStep() {
      if (this.displayPrice <= 0) {
        this.isLoading = true;
        this.isEnrolling = true;
        this.sendCheckoutStats();
        this.enroll(this.customer.infos);
        return Promise.resolve();
      }

      if (!this.$refs.infosStep.stripeIsCompleted) {
        return Promise.resolve();
      }

      const customerInfos = {
        ...this.customer.infos,
        discount_code: this.customer.infos.discount_code || undefined,
      };

      this.isLoading = true;
      this.isEnrolling = true;

      this.sendCheckoutStats();

      return this.$refs.infosStep.$refs.card
        .createPaymentMethod()
        .then((res) => this.enroll({
          ...customerInfos,
          payment_method_id: res.paymentMethod.id,
        }))
        .then((res) => {
          if (!res) {
            return null;
          }

          if (res.requires_action) {
            return this.handlePaymentAction(res.client_secret, customerInfos);
          }

          return null;
        })
        .finally(() => {
          this.isLoading = false;
          this.isEnrolling = false;
        });
    },
    handlePaymentAction(clientSecret, customerInfos) {
      this.isLoading = true;
      this.isEnrolling = true;

      // For subscriptions, use confirmCardPayment; for bundles/training, use handleCardAction
      const stripeMethod = this.buyable.buyable_type === 'MEMBERSHIP_PRICE'
        ? 'confirmCardPayment'
        : 'handleCardAction';

      return this.$refs.infosStep.$refs.card[stripeMethod](clientSecret).then(
        (res) => {
          if (this.buyable.buyable_type === 'MEMBERSHIP_PRICE') {
            // For subscriptions, use same pattern as bundles - pass payment_intent_id for confirmation
            const { paymentIntent } = res;
            return this.enroll({
              ...customerInfos,
              payment_intent_id: paymentIntent.id,
            });
          }
          // For bundles/training, we need to confirm the payment intent
          const { paymentIntent } = res;
          if (paymentIntent.status === 'requires_confirmation') {
            return this.enroll({
              ...customerInfos,
              payment_intent_id: paymentIntent.id,
            });
          }

          return null;
        },
      );
    },
    onPayPalOrder(order_id) {
      const customerInfos = {
        ...this.customer.infos,
        order_id,
        discount_code: this.customer.infos.discount_code || undefined,
      };

      this.isLoading = true;
      this.isEnrolling = true;

      this.sendCheckoutStats();
      this.enroll(customerInfos).finally(() => {
        this.isLoading = false;
        this.isEnrolling = false;
      });
    },
    getUTMData() {
      if (this.isEmbed) {
        return {
          utm_source: 'teachizy-app',
          utm_medium: 'integration-externe',
          utm_campaign: null,
          utm_id: null,
          utm_term: null,
          utm_content: null,
        };
      }

      const utm = {
        utm_source: getLocal(this.$constants.LSKEY_UTM_SOURCE),
        utm_medium: getLocal(this.$constants.LSKEY_UTM_MEDIUM),
        utm_campaign: getLocal(this.$constants.LSKEY_UTM_CAMPAIGN),
        utm_id: getLocal(this.$constants.LSKEY_UTM_ID),
        utm_term: getLocal(this.$constants.LSKEY_UTM_TERM),
        utm_content: getLocal(this.$constants.LSKEY_UTM_CONTENT),
      };

      if (Object.values(utm).every((value) => value == null)) {
        return null;
      }

      return utm;
    },
    waitForMembershipSync() {
      if (this.buyable.buyable_type !== 'MEMBERSHIP_PRICE') {
        return Promise.resolve(true);
      }

      const membershipUuid = this.buyable.subscription?.uuid;
      if (!membershipUuid) {
        return Promise.resolve(true);
      }

      const hasMembershipInStore = () => {
        const memberships = this.$store.state.auth.memberships || [];
        return memberships.some(
          (membership) => membership?.membership?.uuid === membershipUuid,
        );
      };

      if (hasMembershipInStore()) {
        return Promise.resolve(true);
      }

      const attemptFetch = (attempt = 0) => {
        const maxAttempts = 8;

        return this.$store.dispatch('auth/fetchMemberships')
          .then((memberships) => {
            if (
              Array.isArray(memberships)
              && memberships.some(
                (membership) => membership?.membership?.uuid === membershipUuid,
              )
            ) {
              return true;
            }

            if (attempt + 1 >= maxAttempts) {
              return false;
            }

            const baseDelay = attempt < 2 ? 800 : 1500;
            return new Promise((resolve) => {
              setTimeout(() => {
                resolve(attemptFetch(attempt + 1));
              }, baseDelay);
            });
          })
          .catch(() => {
            if (attempt + 1 >= 8) {
              return false;
            }

            const baseDelay = attempt < 2 ? 800 : 1500;
            return new Promise((resolve) => {
              setTimeout(() => {
                resolve(attemptFetch(attempt + 1));
              }, baseDelay);
            });
          });
      };

      return attemptFetch();
    },
  },
  watch: {
    buyable(newVal) {
      this.selectedSubscriptionPriceUuid = newVal.buyable_type === 'MEMBERSHIP_PRICE'
        ? newVal.uuid
        : null;
      this.subscriptionPriceSelectorIsOpen = (
        newVal.buyable_type === 'MEMBERSHIP_PRICE'
        && newVal.subscription
        && Array.isArray(newVal.subscription.prices)
        && newVal.subscription.prices.length > 1
      );
      if (newVal.buyable_type === 'MEMBERSHIP_PRICE') {
        this.newPrice = null;
        this.originalPrice = null;
      }
    },
    selectedSubscriptionPriceUuid(newVal, oldVal) {
      if (
        newVal !== oldVal
        && this.buyable.buyable_type === 'MEMBERSHIP_PRICE'
      ) {
        this.newPrice = null;
        this.originalPrice = null;
      }
    },
  },
  i18n: {
    messages: {
      en: {
        monthly_frequency_name: 'monthly',
        monthly_frequency_description: 'every 30 days',
        biweekly_frequency_name: 'bi-weekly',
        biweekly_frequency_description: 'every 14 days',
        weekly_frequency_name: 'weekly',
        weekly_frequency_description: 'every 7 days',
        bimonthly_frequency_name: 'every two months',
        bimonthly_frequency_description: 'every 2 months',
        quarterly_frequency_name: 'quarterly',
        quarterly_frequency_description: 'every 3 months',
        semesterly_frequency_name: 'semiannual',
        semesterly_frequency_description: 'every 6 months',
        yearly_frequency_name: 'annual',
        yearly_frequency_description: 'every year',
        training_already_owned: 'You already own this training!',
        pack_already_owned: 'You already own this bundle!',
        subscription_already_owned: 'You already own this membership!',
        view_training: 'Discover the training',
        view_pack: 'Discover the bundle',
        view_subscription: 'Discover the membership',
        stay_here: 'Stay here',
        welcome_to_training: 'Welcome to the training!',
        welcome: 'Welcome!',
        view_trainings: 'Discover trainings',
        free: 'Free',
        payments_made: 'Payments are processed',
        hide_prices: 'hide prices',
        show_prices: 'show prices',
        single_payment: '1 single payment of {price}',
        multiple_payments: '{count} payments of {amount}',
        processing: 'Processing...',
        enrollment_closed: 'Enrollments are currently closed.',
        training_full: 'The training is full.',
        membership_activation_pending: 'Your membership is being activated... you will be redirected shortly.',
      },
      fr: {
        monthly_frequency_name: 'mensuel',
        monthly_frequency_description: 'tous les 30 jours',
        biweekly_frequency_name: 'bimensuel',
        biweekly_frequency_description: 'tous les 14 jours',
        weekly_frequency_name: 'hebdomadaire',
        weekly_frequency_description: 'tous les 7 jours',
        bimonthly_frequency_name: 'tous les deux mois',
        bimonthly_frequency_description: 'tous les 2 mois',
        quarterly_frequency_name: 'trimestriel',
        quarterly_frequency_description: 'tous les 3 mois',
        semesterly_frequency_name: 'semestriel',
        semesterly_frequency_description: 'tous les 6 mois',
        yearly_frequency_name: 'annuel',
        yearly_frequency_description: 'tous les ans',
        training_already_owned: 'Vous possédez déjà cette formation !',
        pack_already_owned: 'Vous possédez déjà ce pack !',
        subscription_already_owned: 'Vous possédez déjà cet abonnement !',
        view_training: 'Voir la formation',
        view_pack: 'Voir le pack',
        view_subscription: 'Voir l\'abonnement',
        stay_here: 'Rester ici',
        welcome_to_training: 'Bienvenue dans la formation !',
        welcome: 'Bienvenue !',
        view_trainings: 'Voir les formations',
        free: 'Gratuit',
        payments_made: 'Paiements effectués',
        hide_prices: 'masquer les prix',
        show_prices: 'afficher les prix',
        single_payment: '1 seul paiement de {price}',
        multiple_payments: '{count} paiements de {amount}',
        processing: 'Traitement en cours...',
        enrollment_closed: 'Les inscriptions sont actuellement fermées.',
        training_full: 'La formation est complète.',
        membership_activation_pending: 'Votre abonnement est en cours d\'activation... vous allez être redirigé dans un instant.',
      },
    },
  },
};
</script>

<template>
  <component :is="hasModal ? 'b-modal' : 'div'" class="compmodal" width="520px" :can-cancel="['escape', 'outside']"
    @close="$emit('close')" active>
    <form class="compmodal_box is-relative box p-0 mb-0" @submit.prevent="dataIsValid(isLoading) && handleNext()">
      <button v-if="hasCloseBtn" type="button" class="compmodal_close delete is-absolute" @click="$emit('close')" />

      <header class="compmodal_header is-flex p-8 has-background-light is-custom">
        <figure v-if="buyable.picture" class="compmodal_img image flex-none mt-1 is-hidden-mobile">
          <img class="bradius-4 w-full h-auto mx-auto" :src="`${$env.apiResourcesURL}/${buyable.picture}`"
            :alt="buyableName || buyable.name" />
        </figure>

        <div class="ml-4">
          <h1 class="title is-custom is-4 is-size-6-touch mb-0">
            {{ buyableName || buyable.name }}
          </h1>
          <h2 class="is-size-8 has-text-weight-normal">
            {{ store.name }}
          </h2>
          <p class="mt-3 is-size-6 is-size-8-touch has-text-primary is-custom">
            <template v-if="displayPrice > 0">
              <template v-if="chosenPaymentPlan">
                {{
                  $t("multiple_payments", {
                    count: chosenPaymentPlan.nb_schedule,
                    amount: $options.filters.formatPrice(displayPrice)
                })
                }}
              </template>
              <template v-else>
                {{ displayPrice | formatPrice }}
                <template v-if="buyable.buyable_type === 'MEMBERSHIP_PRICE'">
                  {{ formatIntervalPer(currentIntervalSource, ($i18n && $i18n.locale) || 'fr') }}
                </template>
              </template>
            </template>
            <template v-else>
              {{ $t("free") }}
            </template>
          </p>
          <p v-if="
            newPrice != null || (!chosenPaymentPlan && hasDiscountPrice)
          " class="is-size-8 has-text-black is-cutoms opacity-70 tdecoration-line-through">
            <template v-if="oldPrice > 0">
              <template v-if="chosenPaymentPlan">
                {{
                  $t("multiple_payments", {
                    count: chosenPaymentPlan.nb_schedule,
                    amount: $options.filters.formatPrice(oldPrice)
                  })
                }}
              </template>
              <template v-else>
                {{ oldPrice | formatPrice }}
                <template v-if="buyable.buyable_type === 'MEMBERSHIP_PRICE'">
                  {{ formatIntervalPer(currentIntervalSource, ($i18n && $i18n.locale) || 'fr') }}
                </template>
              </template>
            </template>
            <template v-else>
              {{ $t("free") }}
            </template>
          </p>
          <p v-if="
            !chosenPaymentPlan &&
            hasDiscountPrice &&
            currentDiscountReason
          " class="opacity-70 is-size-8">
            {{ currentDiscountReason }}
          </p>
          <p v-if="chosenPaymentPlan" class="opacity-70 is-size-8">
            {{ $t("payments_made") }}
            {{
              $t(messages.frequencies[chosenPaymentPlan.frequency].description)
            }}
          </p>
        </div>
      </header>
      <main class="compmodal_main p-8">
        <template v-if="buyable.buyable_type === 'MEMBERSHIP_PRICE' && hasSubscriptionPriceOptions">
          <p class="content mb-3 has-text-right tdecoration-underline">
            <a href="#" @click.prevent="toggleSubscriptionPriceSelector">
              {{ subscriptionPriceSelectorIsOpen ? $t('hide_prices') : $t('show_prices') }}
            </a>
          </p>
          <div v-if="subscriptionPriceSelectorIsOpen" class="panel mb-5">
            <div v-for="priceOption in subscriptionPrices" :key="priceOption.uuid" class="panel-block p-3 is-block">
              <b-radio v-model="selectedSubscriptionPriceUuid" :native-value="priceOption.uuid" class="mr-0"
                @input="onSubscriptionPriceChange">
                <span class="has-text-weight-bold">
                  {{ formatSubscriptionPrice(priceOption) }}
                </span>
                <span v-if="subscriptionPriceHasDiscount(priceOption)" class="is-size-7 has-text-grey-dark ml-2">
                  <s>{{ $options.filters.formatPrice(priceOption.amount) }}</s>
                </span>
                <span class="is-size-8 is-block has-text-grey-dark">
                  {{ formatIntervalPer(priceOption, ($i18n && $i18n.locale) || 'fr') }}
                </span>
                <span v-if="subscriptionPriceHasDiscount(priceOption) && priceOption.discount_price_reason"
                  class="is-size-7 is-block has-text-black">
                  {{ priceOption.discount_price_reason }}
                </span>
              </b-radio>
            </div>
          </div>
        </template>

        <template v-if="
          isNotPercentPlan &&
          hasPaymentPlans &&
          paymentSchedules.length > 0
        ">
          <p class="content mb-3 has-text-right tdecoration-underline">
            <a href="#" @click="togglePriceSelector">
              {{ priceSelectorIsOpen ? $t("hide_prices") : $t("show_prices") }}
            </a>
          </p>
          <div v-if="priceSelectorIsOpen" class="panel">
            <div class="panel-block p-3">
              <b-radio :native-value="null" v-model="chosenPaymentPlan" class="mr-0">
                <span class="has-text-weight-bold">
                  {{
                    $t("single_payment", {
                      price: $options.filters.formatPrice(price)
                    })
                  }}
                </span>
              </b-radio>
            </div>
            <div v-for="plan in paymentSchedules" :key="plan.id" class="panel-block p-3">
              <b-radio :native-value="plan" v-model="chosenPaymentPlan">
                <span class="has-text-weight-bold">
                  {{
                    $t("multiple_payments", {
                      count: plan.nb_schedule,
                      amount: $options.filters.formatPrice(plan.amount)
                    })
                  }}
                </span>
                <br />
                <span class="is-size-8 is-size-9-mobile">
                  {{ $t("payments_made") }}
                  {{ $t(messages.frequencies[plan.frequency].description) }}
                </span>
              </b-radio>
            </div>
          </div>
        </template>

        <RegisterFormStepEmail v-if="step == 'email'" :is-loading="isLoading || isEnrolling"
          @update="customer.email = $event" />
        <RegisterFormStepInfos v-else-if="step == 'infos'" ref="infosStep" :is-loading="isLoading || isEnrolling"
          :temp-customer="tempCustomer" :payment-plan="chosenPaymentPlan" :buyable="activeSubscriptionPrice || buyable"
          :base-price="price" :selected-price-uuid="getSelectedMembershipPriceUuid()" @new-price="newPrice = $event"
          @original-price="originalPrice = $event" @update="customer.infos = $event" @paypal-order="onPayPalOrder" />
      </main>

      <div v-show="isEnrolling" class="compmodal_loading is-size-4 has-text-weight-bold">
        {{ $t("processing") }}
        <span class="loader is-primary is-size-2 mt-5" />
      </div>
    </form>
  </component>
</template>

<style lang="scss" scoped>
.compmodal {
  &_close {
    top: pxToRem(8);
    right: pxToRem(8);
  }

  &_img {
    width: 112px;
  }

  &_box {
    overflow: hidden;
  }

  &_loading {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: absolute;
    z-index: 999;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba($white, 0.95);
  }

  @include mobile {
    &_box {
      overflow: auto;
      min-height: 100%;
      border-radius: 0;
    }

    &_header,
    &_main {
      padding-left: pxToRem(12) !important;
      padding-right: pxToRem(12) !important;
    }

    ::v-deep .modal-content,
    ::v-deep .animation-content {
      max-height: none;
      min-height: 100vh;
      width: 100%;
    }
  }
}
</style>
